import {
  RegistrationRequest,
  RegistrationRequestType,
  UploadData,
  useUploadStore,
} from '../../../../../../../store/uploadStore';
import { Loader } from '@traceair/tds';
import { stylesheet } from 'typestyle';
import { CalculatedUploadState } from '../../../../tabs/card/useCalculatedUploadState';
import { AbstractFileIcon } from '../../../../../../../images/components/AbstractFileIcon';
import { CloseIcon, DeleteIcon } from '@traceair/webapp-icons';
import { UploadNotRestoredError } from '../../../../../../../errors/UploadNotRestoredError';
import { calculateRemainingTimeSeconds, formatRemainingTime } from '../../../../helpers/remainingTime';
import { UploadType } from '../../../../../../../consts/uploadType';
import { formatMegabytesProgress } from '../../../../../../../libs/formatMegabytesProgress';

const css = stylesheet({
  iconFile: {
    flex: '0 0 auto',
  },
});

/**
 * For LiDAR uploads (hundreds of small files plus a few multi-GB point clouds) the
 * `X / Y files` counter freezes for minutes on a big file and makes pilots think the
 * upload is stuck, so instead we show uploaded-of-total megabytes, which keeps moving.
 * Every other upload type keeps the file counter.
 *
 * `uploadSize` (the total) is cached at registration time, but `uploadedSize` only once
 * bytes start flowing. So we key off the total alone and treat a missing uploaded value
 * as 0: during registration it reads "0 / X MB" and then counts up, instead of showing a
 * file count that flips to a size the moment the upload starts. While the total itself is
 * still unknown (interrupted / restored uploads), we keep the file counter.
 */
const getUploadProgressTitle = (upload: UploadData | undefined) => {
  const fileCountTitle = `${upload?.overview?.photosCount.uploaded} / ${upload?.overview?.photosCount.all} files`;

  if (upload?.meta.type !== UploadType.Lidar) {
    return fileCountTitle;
  }

  const { uploadSize, uploadedSize } = upload.meta;
  if (uploadSize === undefined) {
    return fileCountTitle;
  }

  return formatMegabytesProgress(uploadedSize ?? 0, uploadSize);
};

export const getProgressUiElements = (
  uploadState: CalculatedUploadState | null,
  registrationRequests: Map<Promise<unknown>, RegistrationRequest> | undefined,
  upload: UploadData | undefined,
  bytesPerSecond: number | undefined,
  fileRegistrationTimeMs: number,
) => {
  const requests = Array.from(registrationRequests?.values() ?? []);

  if (uploadState === CalculatedUploadState.Interrupted) {
    return {
      title: getUploadProgressTitle(upload),
      note: 'Please upload the missing files. You can select all files — the upload will resume from where it left off.',
      shortNote: 'Please upload the missing files.',
      icon: <AbstractFileIcon className={css.iconFile} />,
      isWarning: true,
      removeIcon: <DeleteIcon />,
      disableCancel: false,
    };
  }

  if (uploadState === CalculatedUploadState.Loading) {
    if (requests.some(({ type }) => type === RegistrationRequestType.dateConfirmation)) {
      return {
        title: 'Preparing upload',
        note: 'Verifying date...',
        icon: <Loader />,
        isWarning: false,
        removeIcon: <CloseIcon />,
        disableCancel: false,
      };
    }

    if (requests.some(({ type }) => type === RegistrationRequestType.uploadRegistration)) {
      return {
        title: 'Preparing upload',
        note: 'Registering upload...',
        icon: <Loader />,
        isWarning: false,
        removeIcon: <CloseIcon />,
        disableCancel: true, // Cancel requests work faster than register requests, so we can end up having empty uploads
      };
    }

    if (requests.some(({ type }) => type === RegistrationRequestType.ppkGeorefRegistration)) {
      return {
        title: 'Preparing upload',
        note: 'Registering georef...',
        icon: <Loader />,
        isWarning: false,
        removeIcon: <CloseIcon />,
        disableCancel: false,
      };
    }

    if (!upload) {
      throw new UploadNotRestoredError();
    }

    const isRegisteringFiles = requests.some(({ type }) => type === RegistrationRequestType.batchRegistration);
    let note = 'Registering files...';
    let tooltip: string | undefined = undefined;

    if (!isRegisteringFiles && upload.queue.length) {
      const isBeingUploaded = upload.queue.some(({ isBeingUploaded }) => isBeingUploaded);
      if (isBeingUploaded) {
        const remainingTimeSeconds = calculateRemainingTimeSeconds(bytesPerSecond, fileRegistrationTimeMs, upload);

        note = formatRemainingTime(remainingTimeSeconds);
      } else {
        const hasOtherActiveUploads = Object.values(useUploadStore.getState().uploads).some(
          ({ queue }) => upload.queue !== queue && queue.some((file) => file.isBeingUploaded),
        );
        if (hasOtherActiveUploads) {
          note = 'Pending';
          tooltip =
            'Scan and pano files are first validated, then uploaded one site at a time in the order they arrive, ensuring the first upload finishes first. This allows us to start processing them as soon as possible.';
        }
      }
    }

    return {
      title: getUploadProgressTitle(upload),
      note: note,
      icon: <Loader />,
      isWarning: false,
      removeIcon: <CloseIcon />,
      disableCancel: false,
      tooltip,
    };
  }

  return {
    title: 'All files uploaded',
    note: `${upload!.overview.photosCount.uploaded} files`,
    icon: <AbstractFileIcon className={css.iconFile} />,
    isWarning: false,
    removeIcon: <DeleteIcon />,
    disableCancel: false,
  };
};
